import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { Type } from '~/component/form/input/enums/type.enum';
import { ModalEditTimeslotsComponent } from '~/component/modal-edit-timeslots/modal-edit-timeslots.component';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalRoleEnum } from '~/component/modal/enums/role.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { TimeslotUpdateService } from '~/organizer/timeslot-edition/services/timeslot-update.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

@Component({
  selector: 'app-organizer-timeslot-edition-group-size',
  templateUrl: './group-size.component.html',
})
export class OrganizerTimeslotEditionGroupSizeComponent implements OnInit {
  @ViewChild('modalEditTimeslots')
  public modalEditTimeslots: ModalEditTimeslotsComponent;

  private readonly subscriptions = new Subscription();
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly inputType = Type.Number;
  public readonly minimumGroupSize = 1;
  public readonly modalRole = ModalRoleEnum;
  public readonly modalVariant = ModalVariantEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;
  public readonly toggleNameEnum = ToggleNameEnum;

  public activity: Activity;
  public groupSize: FormControl;
  public isLastTimeslot: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public previousUrl: string;
  public timeslot: Timeslot;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly localizeRouter: LocalizeRouterService,
    private readonly router: Router,
    private readonly timeslotUpdateService: TimeslotUpdateService
  ) {}

  public ngOnInit(): void {
    const { timeslotData } = this.activatedRoute.snapshot.data;

    this.activity = timeslotData.activity;
    this.timeslot = timeslotData.timeslot;
    this.previousUrl = `/organizer.timeslot.manage/${this.timeslot.identifier}`;
    this.groupSize = new FormControl(this.timeslot.maximumAttendeeCapacity, [
      Validators.required,
      this.maximumAttendeeCapacityValidator(
        this.timeslot.maximumAttendeeCapacity -
          this.timeslot.remainingAttendeeCapacity
      ),
    ]);

    this.subscriptions.add(
      this.activityService
        .getTimeslots({
          activityIdentifier: timeslotData.activity.sku,
          startDate: timeslotData.timeslot.startDate,
        })
        .subscribe((upcomingTimeslots) => {
          this.isLastTimeslot = upcomingTimeslots?.length === 1;
        })
    );
  }

  private get maximumAttendeeCapacity(): number {
    return parseInt(this.groupSize.value, 10);
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.timeslot?.maximumAttendeeCapacity);
  }

  public get isFormValid(): boolean {
    return this.groupSize.valid && this.groupSize.dirty;
  }

  private maximumAttendeeCapacityValidator(
    numberOfAttendees: number
  ): ValidatorFn {
    return (control: AbstractControl): Validators | null => {
      return control.value < numberOfAttendees
        ? { maximumAttendeeCapacityInvalid: true }
        : null;
    };
  }

  public applyToSession(): void {
    this.modalEditTimeslots.closeModal();
    this.isOverlayDisplayed = true;

    const partialBody = {
      maximumAttendeeCapacity: this.maximumAttendeeCapacity,
    };

    this.subscriptions.add(
      this.activityService
        .patchTimeslot(this.timeslot.identifier, partialBody)
        .pipe(
          mergeMap(() => {
            return this.activityService
              .getTimeslot(this.timeslot.identifier)
              .pipe(
                repeatUntil(
                  (timeslot) =>
                    timeslot.maximumAttendeeCapacity ===
                    partialBody.maximumAttendeeCapacity
                )
              );
          })
        )
        .subscribe(() => {
          this.overlayStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }

  public updateAllTimeslots(): void {
    this.modalEditTimeslots.closeModal();
    this.isOverlayDisplayed = true;

    const partialBody = {
      maximumAttendeeCapacity: this.maximumAttendeeCapacity,
    };

    this.subscriptions.add(
      combineLatest([
        this.activityService
          .getActivityTimeslots(this.activity?.sku?.toString(), {
            startDate: this.timeslot?.startDate?.toString(),
          })
          .pipe(
            mergeMap((timeslots) => {
              return this.finishPatchTimeslot(timeslots, partialBody);
            })
          ),
        this.activityService
          .getTimeslot(this.timeslot.identifier)
          .pipe(
            repeatUntil(
              (timeslot) =>
                timeslot.maximumAttendeeCapacity ===
                partialBody.maximumAttendeeCapacity
            )
          ),
      ]).subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      })
    );
  }

  public finishPatchTimeslot(timeslots, partialBody): Observable<string[]> {
    return this.activityService.patchTimeslotBulk(
      this.activity?.sku?.toString(),
      timeslots.map((timeslot) => ({
        ...partialBody,
        identifier: timeslot.identifier,
      }))
    );
  }

  public updateSelectedTimeslots(): void {
    this.timeslotUpdateService.setPartialTimeslotBody({
      maximumAttendeeCapacity: this.maximumAttendeeCapacity,
    });

    this.timeslotUpdateService.setPartialTimeslotUpdates({
      maximumAttendeeCapacity: this.maximumAttendeeCapacity,
      remainingAttendeeCapacity:
        this.maximumAttendeeCapacity -
        (this.timeslot.maximumAttendeeCapacity -
          this.timeslot.remainingAttendeeCapacity),
    });

    const translatedPath = this.localizeRouter.translateRoute([
      '/organizer.timeslot.manage',
      this.timeslot.identifier,
      'organizer.timeslot-edition.group-size',
      'organizer.timeslot-edition.select-timeslots',
    ]) as string[];

    this.router.navigate(translatedPath);
  }

  public validateForm(): void {
    this.modalEditTimeslots.openModal();
  }
}
